import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Outlet,
  useParams,
  useNavigate,
  generatePath,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { PermissionEnum } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import ExportAccountsColumn from '../../components/export-column/column-accounts';
import ROUTES from '../../routes';
import { useLazyGetExportPspAccountsQuery } from './store/services';
import { setSelectedAccount } from './store/slices/batches';

import cn from './styles.module.css';

const ExportAccounts = (): React.JSX.Element => {
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);

  const { bankId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { sendMessage } = useOutletContext<{ sendMessage: SendMessage }>();

  const {
    export: {
      accounts: { accounts, pagination },
    },
    transactionsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const [getTransactionsAccountsList, { isFetching, isLoading }] =
    useLazyGetExportPspAccountsQuery();

  const getTransactionsAccountsListAction = useCallback(() => {
    getTransactionsAccountsList({ id: bankId as string, query: '?take=25' });
  }, [bankId]);

  const onSelectAcc = (accountId: string, accountNumber: string) => {
    dispatch(
      setSelectedAccount({
        accountId,
        accountNumber,
      }),
    );
  };

  const onScroll = async (e: any) => {
    const { scrollTop } = e.target;

    if (scrollRef && scrollRef.current) {
      const { scrollHeight, clientHeight } = scrollRef.current;

      if (
        pagination?.hasNextPage &&
        scrollTop >= scrollHeight - clientHeight - 20
      ) {
        setNextPageIsLoading(true);

        const response = await getTransactionsAccountsList({
          id: bankId as string,
          query: `?take=25&cursor=${pagination.endCursor}`,
        });

        if (response?.data?.data) {
          setNextPageIsLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    if (accounts.length) {
      navigate(
        generatePath(ROUTES.EXPORT_ACCOUNTS, {
          bankId: bankId as string,
          accountId: accounts[0].id,
        }),
        {
          replace: true,
        },
      );

      onSelectAcc(accounts[0].id, accounts[0].name);
    }
  }, [accounts]);

  useEffect(() => {
    if (bankId && activePermissions?.includes(PermissionEnum.PSP_LIST)) {
      getTransactionsAccountsListAction();
    }
  }, [getTransactionsAccountsListAction, bankId, activePermissions]);

  return (
    <>
      <div className={cn.columnWrapper}>
        <ExportAccountsColumn
          accounts={accounts}
          bankId={bankId}
          isLoading={isLoading}
          isFetching={isFetching}
          onClick={onSelectAcc}
          activePermissions={activePermissions}
          scrollRef={scrollRef}
          onScroll={onScroll}
          nextPageIsLoading={nextPageIsLoading}
        />
      </div>
      <Outlet context={{ sendMessage }} />
    </>
  );
};

export default ExportAccounts;
